import { isValid } from 'date-fns';
import { useCallback, useRef } from 'react';

import type { TimeValue } from '../../../core/types/time.js';
import { _isOutOfBounds } from '../../calendar/state/calendar-validation.js';
import type { DateTimeError, DateTimeInputProps } from '../types.js';

export const useDateTimeInputValidation = (
  validationProps: Pick<
    DateTimeInputProps,
    'onError' | 'min' | 'max' | 'required'
  >,
  parts: Intl.DateTimeFormatPart[],
  toDateTime: (parts: Intl.DateTimeFormatPart[]) => Date | null,
) => {
  const { onError, min, max, required } = validationProps;

  const errorType = useRef<DateTimeError>(null);

  const updateError = useCallback(
    (newErrorType: DateTimeError) => {
      if (newErrorType !== errorType.current) {
        errorType.current = newErrorType;
        onError?.(newErrorType);
      }
    },
    [onError],
  );

  /*
   * Validates and updates error states from expression strings.
   */
  const validateExpression = useCallback(
    (value?: TimeValue | null, expressionValue?: string) => {
      if (value === null && expressionValue) {
        updateError('invalid');
      } else if ((value === null || value === undefined) && required) {
        updateError('valueMissing');
      } else if (_isOutOfBounds(value?.value, min, max)) {
        updateError('outsideRange');
      } else {
        updateError(null);
      }
    },
    [max, min, required, updateError],
  );

  /*
   * Validates and updates error states for dates from SpinButtons.
   */
  const validateDate = useCallback(
    (value?: TimeValue | null, date?: Date | null) => {
      if (!isValid(date) && date !== null) {
        updateError('invalid');
      } else if (
        (value === null || value === undefined) &&
        date === null &&
        required
      ) {
        updateError('valueMissing');
      } else if (date && _isOutOfBounds(date, min, max)) {
        updateError('outsideRange');
      } else {
        updateError(null);
      }
    },
    [max, min, required, updateError],
  );

  const validateTimeValue = useCallback(
    (value?: TimeValue | null, expressionValue?: string) => {
      const date = toDateTime(parts);
      if (expressionValue && date === null) {
        validateExpression(value, expressionValue);
      } else {
        validateDate(value, date);
      }
    },
    [parts, toDateTime, validateDate, validateExpression],
  );

  return {
    validateTimeValue,
  };
};
